<!--
Copyright (c) 2020-2026, NVIDIA CORPORATION.  All rights reserved.

NVIDIA CORPORATION and its licensors retain all intellectual property
and proprietary rights in and to this software, related documentation
and any modifications thereto.  Any use, reproduction, disclosure or
distribution of this software and related documentation without an express
license agreement from NVIDIA CORPORATION is strictly prohibited.
-->
<div
    class="hig-bottom-sheet"
    [class.is-tv]="tvMode"
    [fxLayout]="layoutType"
    fxLayoutAlign="space-between stretch"
    role="dialog"
    aria-modal="true"
    nvSpatialNavigationContainer
    nvTrapFocus
    [attr.aria-label]="data?.headerText?.text || data?.bodyText?.text | translate"
>
    <!-- icon, qrcode -->
    @if (hasImage) {
        <div class="image-container">
            @if (data.icon) {
                <i class="material-icons">{{ data.icon }}</i>
            }
            @if (data.image) {
                <img alt="" [src]="data.image.url" />
            }
            @if (showQRCode) {
                <qrcode
                    role="img"
                    [attr.aria-label]="'common.qrCode' | translate"
                    [qrdata]="data.link.url"
                    [width]="tvMode ? 184 : 140"
                    [errorCorrectionLevel]="'M'"
                ></qrcode>
            }
        </div>
    }
    <!-- header, text, error, link -->
    <div
        [fxFlex]="tvMode ? '' : 'grow'"
        class="content"
        [ngClass]="{ 'has-image': hasImage, 'only-body': !data.headerText }"
        fxLayout="column"
        fxLayoutAlign="start start"
        [fxLayoutGap]="tvMode ? '30px' : '16px'"
    >
        @if (data.headerText) {
            <div
                fxFlex="nogrow"
                class="header-text"
                [ngClass]="tvMode ? 'font-header5' : 'font-header6'"
                role="heading"
                aria-level="2"
            >
                {{ data.headerText.text | translate: data.headerText.placeholders }}
            </div>
        }
        <div
            fxFlex
            fxLayout="row"
            [fxLayoutAlign]="data.headerText ? 'start start' : 'start center'"
            fxLayoutGap="24px"
        >
            @if (data.showSpinner) {
                <mat-progress-spinner
                    fxFlex="none"
                    mode="indeterminate"
                    color="accent"
                    diameter="64"
                    [attr.aria-label]="'loading' | translate"
                ></mat-progress-spinner>
            }
            <div fxFlex fxLayout="column" class="font-body2">
                @if (data.bodyText) {
                    <div
                        fxFlex="auto"
                        class="body-text"
                        [ngClass]="{ 'overflow-wrap-anywhere': showQRCode }"
                        [innerHTML]="
                            data.bodyText.translated
                                ? data.bodyText.text
                                : (data.bodyText.text | translate: data.bodyText.placeholders)
                        "
                    ></div>
                }
                @if (data.errorSubtext) {
                    <div fxFlex="auto" class="body-text" [ngClass]="{ 'hig-dialog-storage-region-text': tvMode }">
                        {{ data.errorSubtext.text | translate: data.errorSubtext.placeholders }}
                    </div>
                }
                @if (data.errorCodeText) {
                    <div fxFlex="auto" class="body-text" [ngClass]="{ 'font-tv-error-text': tvMode }">
                        {{ data.errorCodeText.text | translate: data.errorCodeText.placeholders }}
                    </div>
                }
            </div>
        </div>
    </div>
    <div
        [fxFlex]="tvMode ? '' : 'initial'"
        [fxFlexAlign]="tvMode ? 'start' : 'end'"
        class="actions-container"
        fxLayout="column"
    >
        <div
            class="buttons-container"
            [fxLayout]="tvMode ? 'column' : 'row'"
            [fxLayoutAlign]="tvMode ? '' : 'end center'"
            [fxLayoutGap]="tvMode ? '16px' : '8px'"
        >
            @if (tvMode) {
                <ng-container [ngTemplateOutlet]="primaryButton"></ng-container>
                <ng-container [ngTemplateOutlet]="secondaryButton"></ng-container>
            } @else {
                <ng-container [ngTemplateOutlet]="secondaryButton"></ng-container>
                <ng-container [ngTemplateOutlet]="primaryButton"></ng-container>
            }
        </div>

        <ng-template #primaryButton>
            @if (data.primaryButton) {
                <button
                    mat-button
                    [ngClass]="showAccentBtn ? 'font-button2 mat-flat-button mat-accent' : 'font-button2'"
                    [nvFocusRipple]="showAccentBtn"
                    (click)="onPrimaryButtonClick($event)"
                    [disabled]="data.primaryButton.isDisabled"
                    [nvAutoFocus]="autoFocusOnPrimaryButton"
                >
                    {{ data.primaryButton.text | translate }}
                    <ng-container
                        [ngTemplateOutlet]="buttonSpinner"
                        [ngTemplateOutletContext]="{ visible: data.primaryButton.spinnerOverlay }"
                    ></ng-container>
                </button>
            }
        </ng-template>

        <ng-template #secondaryButton>
            @if (data.secondaryButton) {
                <button
                    mat-button
                    class="font-button2"
                    (click)="onSecondaryButtonClick($event)"
                    [disabled]="data.secondaryButton.isDisabled"
                >
                    {{ data.secondaryButton.text | translate }}
                    <ng-container
                        [ngTemplateOutlet]="buttonSpinner"
                        [ngTemplateOutletContext]="{ visible: data.secondaryButton.spinnerOverlay }"
                    ></ng-container>
                </button>
            }
        </ng-template>

        <ng-template #buttonSpinner let-visible="visible">
            @if (visible) {
                <div fxLayout="column" fxLayoutAlign="center end" class="spinner-overlay">
                    <mat-progress-spinner
                        class="mat-accent"
                        mode="indeterminate"
                        [diameter]="48"
                        aria-hidden="true"
                    ></mat-progress-spinner>
                </div>
            }
        </ng-template>
    </div>
</div>
